import React, { useState } from "react";
import BlockKitStore from "../../stores/BlockKitStore";
import { inject, observer } from "mobx-react";
import { BlockJsonModel } from "../../models/BlockTransportSystemJsonModel";
import { Button, Col, Form, FormGroup, Modal, Row, Stack } from "react-bootstrap";
import { LanguageCode } from "../../constants/LanguageCode";
import { useTranslation } from "react-i18next";

interface Props {
    blockKitStore?: BlockKitStore;
    selectedBlock: BlockJsonModel;
    isShow: boolean;
    onClickApply: (block: BlockJsonModel) => void;
    onClickClose: () => void;
}

const BlockEditModal: React.FC<Props> = inject("blockKitStore")(observer(({
    blockKitStore,
    selectedBlock,
    isShow,
    onClickApply,
    onClickClose}) => {
    const locales = Object.values(LanguageCode)
    const [block, setBlock] = useState<BlockJsonModel>(selectedBlock);
    const [locale, setLocale] = useState<string>(locales[0]);
    const [price, setPrice] = useState<number>(0);
    const { t } = useTranslation();

    const addPrice = () => {
        if (!locale) {
            alert(t("country-selection-message"));
            return;
        }

        if (!price) {
            alert(t("price-invalid-message"));
            return;
        }

        if (block.prices?.some(p => p.locale === locale)) {
            alert(t("price-already-added-message"));
            return;
        }

        setBlock({
            ...block,
            prices: [ ...block.prices, { locale, price }]
        });
        setLocale("");
        setPrice(0);
    }

    const removePrice = (locale: string) => {
        setBlock({
            ...block,
            prices: block.prices.filter(p => p.locale !== locale)
        });
    }

    // 비어 있으면 가격 없음으로 보고 허용하고, 0 이하만 차단한다. 그리드에서 수정한 값은 문자열로 들어올 수 있다.
    const isInvalidPrice = (price: unknown) =>
        price !== undefined && price !== null && price !== "" && !(Number(price) > 0);

    const hasInvalidPrice = (target: BlockJsonModel) =>
        isInvalidPrice(target.price) || (target.prices?.some(p => isInvalidPrice(p.price)) ?? false);

    const save = () => {
        if (hasInvalidPrice(block)) {
            alert(t("price-invalid-message"));
            return;
        }
        onClickApply(block);
    }

    const removeDefaultPrice = () => {
        setBlock({
            ...block,
            price: undefined
        });
    }

    return (
        <Modal show={isShow} onHide={() => onClickClose()}>
            <Modal.Header closeButton>{t("price-setting")}</Modal.Header>
            <Modal.Body>
                <Stack gap={1}>
                    <Form.Group as={Row}>
                        <Form.Label column sm={3}>{t("default-price")}</Form.Label>
                        <Col>
                            <Form.Control
                                type="number"
                                value={block?.price ?? ""}
                                min={0}
                                onChange={(e) =>
                                    setBlock({
                                        ...block,
                                        price: e.target.value === "" ? undefined : Number(e.target.value)
                                    })
                                }/>
                        </Col>
                        <Col sm={2}>
                            <Button variant="danger" onClick={() => removeDefaultPrice()}>{t("button-delete")}</Button>
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Col sm={3}>
                            <Form.Select
                                onChange={(e) => setLocale(e.target.value)}>
                                {locales.map(locale => (
                                    <option key={locale} value={locale}>{locale}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col>
                            <Form.Control
                                type="number"
                                value={price}
                                min={0}
                                onChange={(e) => setPrice(Number(e.target.value))}/>
                        </Col>
                        <Col sm={2}>
                            <Button onClick={(e) => addPrice()}>{t("button-add")}</Button>
                        </Col>
                    </Form.Group>
                    <Form.Label>{t("price-by-country")}</Form.Label>
                    {block?.prices?.map((p, index) => {
                        return (
                            <FormGroup key={index} as={Row}>
                                <Form.Label column sm={3}>{p.locale}</Form.Label>
                                <Col>
                                    <Form.Control type="text" plaintext readOnly value={p.price}/>
                                </Col>
                                <Col sm={2}>
                                    <Button variant="outline-danger" onClick={() => removePrice(p.locale)}>{t("button-delete")}</Button>
                                </Col>
                            </FormGroup>
                        )
                    })}
                </Stack>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => save()}>{t("button-save")}</Button>
                <Button variant="secondary" onClick={() => onClickClose()}>{t("button-close")}</Button>
            </Modal.Footer>
        </Modal>
    )
}))

export default BlockEditModal;